// /src/components/admin/StatsTab.jsx
// Platform Stats - At-a-glance health dashboard
// Read-only statistics from database tables

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { supabaseFetch } from '../../lib/supabase';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import StandardButton from '../core/StandardButton';

const StatsTab = () => {
  const theme = useTheme();
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalArticles: 0,
    totalChats: 0,
    totalGroups: 0,
    totalEvents: 0
  });

  const loadStats = async () => {
    setLoading(true);
    try {
      // Query counts from each table. Every figure is a real count: demo rows live in the `demo`
      // schema, and public cannot hold one (20260918d CHECK), so no demo split is needed.
      const [users, activeUsers, articles, chats, groups, events] = await Promise.all([
        supabaseFetch.query('users', { select: 'id', limit: 10000 }),
        supabaseFetch.query('users', {
          select: 'id',
          gte: { last_sign_in_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString() },
          limit: 10000
        }),
        supabaseFetch.query('articles', { select: 'id', limit: 10000 }),
        supabaseFetch.query('chats', { select: 'id', limit: 10000 }),
        supabaseFetch.query('communities', { select: 'id', limit: 10000 }),
        supabaseFetch.query('articles', { select: 'id', eq: { article_type: 'event' }, limit: 10000 })
      ]);

      setStats({
        totalUsers: users.data?.length || 0,
        activeUsers: activeUsers.data?.length || 0,
        totalArticles: articles.data?.length || 0,
        totalChats: chats.data?.length || 0,
        totalGroups: groups.data?.length || 0,
        totalEvents: events.data?.length || 0
      });
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const styles = {
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '16px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    title: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: theme?.text?.primary || '#1A1A1A'
    },
    refreshButton: {
      padding: '8px 16px',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: theme?.primary || '#024B99',
      color: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      fontWeight: '500'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '16px'
    },
    statBox: {
      backgroundColor: theme?.bg || '#F5F5F5',
      borderRadius: '8px',
      padding: '16px',
      textAlign: 'center'
    },
    statValue: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: theme?.primary || '#024B99',
      marginBottom: '4px'
    },
    statLabel: {
      fontSize: '13px',
      color: theme?.text?.secondary || '#666',
      fontWeight: '500'
    },
    loadingBox: {
      textAlign: 'center',
      padding: '40px',
      color: theme?.text?.secondary || '#666'
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>{t('platformStatistics')}</div>
        <button
          onClick={loadStats}
          disabled={loading}
          style={{
            ...styles.refreshButton,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'default' : 'pointer'
          }}
        >
          <RefreshCw size={14} />
          {t('refresh')}
        </button>
      </div>

      {loading ? (
        <div style={styles.loadingBox}>{t('loadingStatistics')}</div>
      ) : (
        <div style={styles.grid}>
          <div style={styles.statBox}>
            <div style={styles.statValue}>{stats.totalUsers}</div>
            <div style={styles.statLabel}>{t('totalRegisteredUsers')}</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statValue}>{stats.activeUsers}</div>
            <div style={styles.statLabel}>{t('activeUsers30Days')}</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statValue}>{stats.totalArticles}</div>
            <div style={styles.statLabel}>{t('totalArticlesPublished')}</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statValue}>{stats.totalChats}</div>
            <div style={styles.statLabel}>{t('totalChatThreads')}</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statValue}>{stats.totalGroups}</div>
            <div style={styles.statLabel}>{t('totalGroups')}</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statValue}>{stats.totalEvents}</div>
            <div style={styles.statLabel}>{t('totalEvents')}</div>
          </div>
        </div>
      )}

      {/* The Analytics page (AdminAnalytics, view 'admin-analytics'), otherwise reachable only by
          typing #/admin/analytics. English only: the Admin Panel takes no translation keys
          (Michael, 3 Oct 2026; docs/doc-accuracy-register.md, TRANSLATION section). */}
      <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <StandardButton
          variant="utility"
          label="Open Analytics"
          onClick={() => window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'admin-analytics' } }))}
        />
        <span style={{ fontSize: '13px', color: theme?.text?.secondary || '#666' }}>
          Events, sessions and sign-ups — today, 7 or 30 days
        </span>
      </div>
    </div>
  );
};

export default StatsTab;
